import { Info } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import { IndicatorsRecovery } from 'lib/api/collections/types';
import CustomButton from 'lib/designSystem/Button';
import CentralPieChart, {
  CentralPieChartItem,
} from 'lib/designSystem/Charts/CentralPieChart';
import ColoredCircle from 'lib/designSystem/ColoredCircle';
import EnhancedTable from 'lib/designSystem/Table';
import { CHART_COLOR_PALETTES } from 'lib/functions/const/colors';
import React from 'react';
import { useDispatch } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

type RecoveryDialogProps = {
  openDialog: boolean;
  handleDialogClose: () => void;
  recoveriesForInsights: IndicatorsRecovery[];
  selectedSegment: CentralPieChartItem;
  showRecoveryBy: string;
};

const RecoveryDialog = ({
  openDialog,
  handleDialogClose,
  recoveriesForInsights,
  selectedSegment,
  showRecoveryBy,
}: RecoveryDialogProps) => {
  const dispatch = useDispatch();

  const handleShowBusinessDetails = (id: string) => {
    const row = recoveriesForInsights.find(
      (x: IndicatorsRecovery) => x.id === id,
    );
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  const handleShowStakeholderDetails = (id: string) => {
    const row = recoveriesForInsights.find(
      (x: IndicatorsRecovery) => x.id === id,
    );
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.stakeholderIdentifier ?? '',
        name: row.stakeholderName ?? '',
      }),
    );
  };

  const totalRecoveryAmount = recoveriesForInsights.reduce(
    (acc: number, recovery: IndicatorsRecovery) => acc + recovery.amount,
    0,
  );

  const totalRecoveryCount = recoveriesForInsights.length;

  const recoveryByClientChartDataSource = recoveriesForInsights.reduce(
    (
      acc: Record<string, { count: number; amount: number }>,
      recovery: IndicatorsRecovery,
    ) => {
      const clientName = recovery.businessName;
      if (acc[clientName]) {
        acc[clientName] = {
          count: acc[clientName].count + 1,
          amount: acc[clientName].amount + recovery.amount,
        };
      } else {
        acc[clientName] = { count: 1, amount: recovery.amount };
      }
      return acc;
    },
    {},
  );

  const recoveryByClientChartDataProcessed = Object.entries(
    recoveryByClientChartDataSource,
  ).reduce(
    (acc: Record<string, { amount: number; count: number }>, [name, value]) => {
      if (
        showRecoveryBy === 'amount'
          ? value.amount / totalRecoveryAmount > 0.05
          : value.count / totalRecoveryCount > 0.05
      ) {
        acc[name] = value;
      } else {
        acc['Otros'] = {
          amount: (acc['Otros']?.amount || 0) + value.amount,
          count: (acc['Otros']?.count || 0) + value.count,
        };
      }
      return acc;
    },
    {},
  );

  const recoveryByClientChartData = Object.entries(
    recoveryByClientChartDataProcessed,
  ).map(([name, value]) => ({
    name,
    value: value.amount,
  }));

  const countByClientChartData = Object.entries(
    recoveryByClientChartDataProcessed,
  ).map(([name, value]) => ({
    name,
    value: value.count,
  }));

  const recoveryByStakeholderChartDataSource = recoveriesForInsights.reduce(
    (
      acc: Record<string, { count: number; amount: number }>,
      recovery: IndicatorsRecovery,
    ) => {
      const stakeholderName = recovery.stakeholderName;
      if (!stakeholderName) return acc;
      if (acc[stakeholderName]) {
        acc[stakeholderName] = {
          count: acc[stakeholderName].count + 1,
          amount: acc[stakeholderName].amount + recovery.amount,
        };
      } else {
        acc[stakeholderName] = { count: 1, amount: recovery.amount };
      }
      return acc;
    },
    {},
  );

  const recoveryByStakeholderChartDataProcessed = Object.entries(
    recoveryByStakeholderChartDataSource,
  ).reduce(
    (acc: Record<string, { amount: number; count: number }>, [name, value]) => {
      if (
        showRecoveryBy === 'amount'
          ? value.amount / totalRecoveryAmount > 0.05
          : value.count / totalRecoveryCount > 0.05
      ) {
        acc[name] = value;
      } else {
        acc['Otros'] = {
          amount: (acc['Otros']?.amount || 0) + value.amount,
          count: (acc['Otros']?.count || 0) + value.count,
        };
      }
      return acc;
    },
    {},
  );

  const recoveryByStakeholderChartData = Object.entries(
    recoveryByStakeholderChartDataProcessed,
  ).map(([name, value]) => ({
    name,
    value: value.amount,
  }));

  const countByStakeholderChartData = Object.entries(
    recoveryByStakeholderChartDataProcessed,
  ).map(([name, value]) => ({
    name,
    value: value.count,
  }));

  return (
    <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="lg">
      <DialogTitle>
        <ColoredCircle color={selectedSegment?.color ?? ''} size={15} />
        {` ${showRecoveryBy === 'amount' ? 'Monto recuperado' : 'Documentos recuperados'} - ${selectedSegment?.name}`}
      </DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={1} sm={6}>
              <Typography align="center">Por cliente</Typography>
            </Grid>
            <Grid item xs={6} sm={6}>
              <Typography align="center">Por pagador</Typography>
            </Grid>
          </Grid>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
            <CentralPieChart
              data={
                showRecoveryBy === 'amount'
                  ? recoveryByClientChartData
                  : countByClientChartData
              }
              showLegend={false}
              height={220}
              colors={CHART_COLOR_PALETTES.green}
            />
            <CentralPieChart
              data={
                showRecoveryBy === 'amount'
                  ? recoveryByStakeholderChartData
                  : countByStakeholderChartData
              }
              showLegend={false}
              height={220}
              colors={CHART_COLOR_PALETTES.green}
            />
          </div>
          <EnhancedTable
            dataHeaders={[
              { key: 'documentTypeEsp', label: 'Tipo' },
              { key: 'collectorName', label: 'Ejecutivo' },
              { key: 'folio', label: 'Folio' },
              {
                key: 'businessName',
                label: 'Cliente',
                handleAction: handleShowBusinessDetails,
                icon: <Info fontSize="small" />,
              },
              {
                key: 'stakeholderName',
                label: 'Pagador',
                handleAction: handleShowStakeholderDetails,
                icon: <Info fontSize="small" />,
              },
              {
                key: 'paymentDate',
                label: 'Vencimiento',
                type: 'date',
              },
              {
                key: 'paidAt',
                label: 'Pagado',
                type: 'date',
              },
              { key: 'amount', label: 'Monto', type: 'money' },
            ]}
            data={recoveriesForInsights}
            exportToFile="xlsx"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={handleDialogClose}>Cerrar</CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default RecoveryDialog;
